/**
 * Guided-walkthrough overlay.
 *
 * Renders a pulsing highlight ring + a bouncing "comic" arrow pointing at the
 * current step's target, plus an instruction label. The flow is deterministic
 * and LLM-free (see companion-walkthrough-store.ts): the model's only job was
 * deciding to launch it.
 *
 * Advance model — the user must click the REAL control (no "Next" shortcut):
 * clicking step N's target performs the actual action (e.g. opening the
 * settings sheet), which is what makes step N+1's target exist. A "Next" button
 * would let the user skip ahead to a step whose target isn't mounted yet.
 *
 * Positioning — a requestAnimationFrame loop re-resolves the target's rect every
 * frame, so the ring/arrow follow a target that mounts or SLIDES IN (the
 * settings sheet animates in from the right — a CSS transform that fires no
 * mutation/scroll events, so event-driven repositioning alone misses it). While
 * the target is off-screen or unlaid-out the ring hides and only the docked
 * instruction shows.
 *
 * Mounted once in app-layout alongside the CompanionOverlay.
 */

import { useCallback, useEffect, useState } from 'react'
import { panelWindowOf, targetSelector, useWalkthroughStore } from './companion-walkthrough-store'
import { textPx } from '@/features/theme-editor/text-scale'

interface Rect {
  left: number
  top: number
  width: number
  height: number
  /** The control's own corner radius, so the highlight follows its shape. */
  radius: number
}

/** Padding between the control's edge and the highlight outline. */
const RING_PAD = 4
/** Controls up to this size (and roughly square) are icons and get a circle. */
const ICON_MAX = 44

/**
 * Resolve a step target to a live element, anchored to the walkthrough's panel.
 * Scoped to the anchored panel's window first (the gear is in the mosaic
 * toolbar, not the [data-panel-id] body — see panelWindowOf); falls back to a
 * global lookup for targets in a portaled sheet (only one sheet is open).
 */
function resolveElement(target: string, panelId: string | null): HTMLElement | null {
  const sel = targetSelector(target)
  if (panelId) {
    const scoped = panelWindowOf(panelId)?.querySelector<HTMLElement>(sel)
    if (scoped) return scoped
  }
  return document.querySelector<HTMLElement>(sel)
}

function sameRect(a: Rect | null, b: Rect | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.left === b.left &&
    a.top === b.top &&
    a.width === b.width &&
    a.height === b.height &&
    a.radius === b.radius
  )
}

/** Visible = laid out AND within the viewport (not mid slide-in off-screen). */
function visibleRect(el: HTMLElement | null): Rect | null {
  const r = el?.getBoundingClientRect()
  if (!el || !r || r.width === 0 || r.height === 0) return null
  if (r.left >= window.innerWidth || r.right <= 0 || r.top >= window.innerHeight || r.bottom <= 0)
    return null
  // The control's real corner radius (first corner is enough; controls are
  // uniform). jsdom and unstyled elements report '' → 0.
  const radius = parseFloat(getComputedStyle(el).borderTopLeftRadius) || 0
  return { left: r.left, top: r.top, width: r.width, height: r.height, radius }
}

const LABEL_W = 250

/** Deterministic pseudo-random offset in [-1.5, 1.5]: the "roughness" of a hand-drawn stroke. */
function wobble(seed: number, i: number): number {
  const x = Math.sin(seed * 9301 + i * 49297) * 233280
  return (x - Math.floor(x)) * 3 - 1.5
}

/**
 * One stroke of a hand-drawn line: a quadratic curve whose midpoint and end are
 * nudged by a seeded wobble, the way rough.js draws Excalidraw's arrows.
 */
function roughSegment(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  seed: number,
  i: number,
): string {
  const mx = (x1 + x2) / 2 + wobble(seed, i)
  const my = (y1 + y2) / 2 + wobble(seed, i + 1)
  const ex = x2 + wobble(seed, i + 2) * 0.5
  const ey = y2 + wobble(seed, i + 3) * 0.5
  const f = (n: number) => Math.round(n * 10) / 10
  return `M${f(x1)} ${f(y1)} Q${f(mx)} ${f(my)} ${f(ex)} ${f(ey)}`
}

export function CompanionWalkthrough() {
  const active = useWalkthroughStore((s) => s.active)
  const steps = useWalkthroughStore((s) => s.steps)
  const index = useWalkthroughStore((s) => s.index)
  const scope = useWalkthroughStore((s) => s.scope)
  const advance = useWalkthroughStore((s) => s.advance)
  const dismiss = useWalkthroughStore((s) => s.dismiss)

  const step = active ? steps[index] : undefined
  const target = step?.target
  const panelId = scope?.panelId ?? null

  const [rect, setRect] = useState<Rect | null>(null)

  // ── Follow the target every frame (mount + slide-in animations) ────────────
  useEffect(() => {
    if (!target) {
      setRect(null)
      return
    }
    let raf = 0
    const tick = () => {
      const next = visibleRect(resolveElement(target, panelId))
      setRect((prev) => (sameRect(prev, next) ? prev : next))
      raf = requestAnimationFrame(tick)
    }
    tick()
    return () => cancelAnimationFrame(raf)
  }, [target, panelId])

  // ── Advance when the user clicks the current (anchored) target ─────────────
  useEffect(() => {
    if (!active || !target) return
    const onClick = (e: MouseEvent) => {
      const sel = targetSelector(target)
      const hit = (e.target as Element | null)?.closest?.(sel)
      if (!hit) return
      // Panel-local targets (the gear) must be THE anchored panel's instance so
      // a different panel's gear can't drive the flow. Portaled targets (the
      // settings-sheet tabs and the per-row Add buttons — not inside the panel
      // window) advance on ANY match, since only one sheet is open: clicking any
      // column's Add completes the step (don't force the one we happen to ring).
      const scopeEl = panelId ? panelWindowOf(panelId) : null
      const scopedTarget = scopeEl?.querySelector(sel)
      if (!scopedTarget || hit === scopedTarget) {
        requestAnimationFrame(() => advance())
      }
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [active, target, panelId, advance])

  // ── Esc dismisses ──────────────────────────────────────────────────────────
  const handleEsc = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss()
    },
    [dismiss],
  )
  useEffect(() => {
    if (!active) return
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [active, handleEsc])

  if (!active || !step) return null

  const total = steps.length
  const stepNo = index + 1

  const center = rect ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 } : null
  // The highlight follows the control's shape: its box plus a little
  // breathing room. Small icon controls (roughly square, up to ICON_MAX) get
  // a circle; every other control keeps its own corners, so a wide button gets
  // a rounded rectangle or a pill, never a huge circle around it.
  const ringW = rect ? rect.width + RING_PAD * 2 : 0
  const ringH = rect ? rect.height + RING_PAD * 2 : 0
  const iconLike = rect
    ? Math.abs(rect.width - rect.height) <= 6 && Math.max(rect.width, rect.height) <= ICON_MAX
    : false
  const ringRadius = !rect
    ? 0
    : iconLike
      ? 9999
      : Math.min(rect.radius + RING_PAD, Math.min(ringW, ringH) / 2)

  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800

  // Coach-mark (arrow + label) goes BELOW the target when it sits in the upper
  // ~55% of the screen, otherwise ABOVE — so it never runs off the top/bottom.
  const below = center ? center.y < vh * 0.55 : true
  const ARROW_H = 46
  const labelLeft = center
    ? Math.min(Math.max(center.x - LABEL_W / 2, 12), vw - LABEL_W - 12)
    : (vw - LABEL_W) / 2

  const arrowTop = center
    ? below
      ? (rect as Rect).top + (rect as Rect).height + 4
      : (rect as Rect).top - 4 - ARROW_H
    : 0
  const labelTop = center ? (below ? arrowTop + ARROW_H + 4 : arrowTop - 4 - 78) : vh - 120

  return (
    <div
      data-testid="companion-walkthrough"
      style={{ position: 'fixed', inset: 0, zIndex: 10002, pointerEvents: 'none' }}
    >
      <style>{`
        @keyframes wt-pulse {
          0%   { transform: translate(-50%,-50%) scale(1);    box-shadow: 0 0 0 0 hsl(var(--primary) / 0.45); }
          70%  { transform: translate(-50%,-50%) scale(1.06); box-shadow: 0 0 0 6px hsl(var(--primary) / 0); }
          100% { transform: translate(-50%,-50%) scale(1);    box-shadow: 0 0 0 0 hsl(var(--primary) / 0); }
        }
        @keyframes wt-bounce { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
      `}</style>

      {/* Pulsing highlight ring on the target */}
      {rect && center && (
        <div
          data-testid="walkthrough-ring"
          style={{
            position: 'fixed',
            left: center.x,
            top: center.y,
            width: ringW,
            height: ringH,
            borderRadius: ringRadius,
            border: '2px solid hsl(var(--primary))',
            animation: 'wt-pulse 1.4s ease-out infinite',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Bouncing arrow pointing at the target */}
      {rect && center && (
        <div
          data-testid="walkthrough-arrow"
          style={{
            position: 'fixed',
            left: center.x - 11,
            top: arrowTop,
            transform: below ? 'none' : 'rotate(180deg)',
            pointerEvents: 'none',
          }}
        >
          <div style={{ animation: 'wt-bounce 1.1s ease-in-out infinite' }}>
            {/* Hand-drawn arrow in the Excalidraw style: each stroke is drawn
                twice with a small deterministic wobble (seeded by the step so
                it does not jitter between frames), round caps, open head.
                Points UP; parent rotates 180° when the coach-mark sits below. */}
            <svg width="22" height={ARROW_H} viewBox="0 0 22 46" fill="none" aria-hidden>
              {[0, 1].map((pass) => (
                <g
                  key={pass}
                  stroke="hsl(var(--primary))"
                  strokeWidth={pass === 0 ? 2 : 1.4}
                  strokeOpacity={pass === 0 ? 1 : 0.55}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d={roughSegment(11, 43, 11, 12, stepNo + pass * 7, 0)} />
                  <path d={roughSegment(11, 6, 4, 15, stepNo + pass * 7, 4)} />
                  <path d={roughSegment(11, 6, 18, 15, stepNo + pass * 7, 8)} />
                </g>
              ))}
            </svg>
          </div>
        </div>
      )}

      {/* Instruction label */}
      <div
        style={{
          position: 'fixed',
          left: center ? labelLeft : '50%',
          top: center ? labelTop : undefined,
          bottom: center ? undefined : 24,
          transform: center ? undefined : 'translateX(-50%)',
          width: LABEL_W,
          pointerEvents: 'auto',
          background: 'hsl(var(--popover))',
          color: 'hsl(var(--popover-foreground))',
          border: '1px solid hsl(var(--primary) / 0.44)',
          borderRadius: 10,
          padding: '10px 12px',
          boxShadow: '0 10px 30px hsl(0 0% 0% / 0.75)',
          fontSize: textPx(13),
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: textPx(11), fontWeight: 600, color: 'hsl(var(--primary))' }}>
            Step {stepNo} of {total}
          </span>
          {/* Small pill, like the app's ghost buttons: a thin border, a compact
              glyph, a hover fill. The dismiss must not read as a primary action. */}
          <button
            type="button"
            aria-label="Dismiss walkthrough"
            title="Dismiss (Esc)"
            onClick={dismiss}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'hsl(var(--hover))')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 20,
              minWidth: 28,
              padding: '0 8px',
              border: '1px solid hsl(var(--border-strong))',
              borderRadius: 9999,
              background: 'transparent',
              color: 'hsl(var(--muted-foreground))',
              cursor: 'pointer',
              fontSize: textPx(10),
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
        <p style={{ margin: '5px 0 0', lineHeight: 1.35 }}>
          {step.instruction}
          {!rect && ' …'}
        </p>
      </div>
    </div>
  )
}
